import { LocationType } from "@/types/data"
import _ from "lodash"

export function getDeviceIcon(value: string) {
    switch (value) {
        case "OTHER":
            return "icon icon-washing-machine"
        case "EV_CHARGER":
            return "icon icon-car"
        case "SOLAR_POWER_PLANT":
            return "icon icon-sun"
        case "BATTERY":
            return "icon icon-battery"
        case "HYBRID_INVERTER":
            return "icon icon-hybrid"
        case "HVAC":
            return "icon icon-heat-pump"
        case "GRID":
            return "icon icon-system"
        default:
            return "icon icon-other"
    }
}

export function getLocationTypeText(type: LocationType) {
    switch (type) {
        case LocationType.Residential:
            return "Residential"
        case LocationType.Industrial:
            return "Industrial"
        case LocationType.Other:
            return "Other"
    }
}

export function getValidationClasses(classes: {
    touched?: "touched"
    untouched?: "untouched"
    valid?: "valid"
    invalid?: "invalid"
    pristine?: "pristine"
    dirty?: "dirty" // control has been interacted with
}) {
    const isInvalid = classes?.invalid
    const isValid = classes?.valid

    return {
        "is-invalid": isInvalid,
        "is-valid": isValid,
    }
}

export function processRoundingErrorsInPercentages(l: Array<number>, target: number) {
    const off =
        target -
        _.reduce(
            l,
            function (acc: number, x: number) {
                return acc + Math.round(x)
            },
            0,
        )

    return _.chain(l)
        .sortBy(function (x: number) {
            return Math.round(x) - x
        })
        .map(function (x: number, i: number) {
            return Math.round(x) + (off > i ? 1 : 0) - (i >= l.length + off ? 1 : 0)
        })
        .value()
}

export function flipSign(value: number | null): number | null {
    if (value === null || value == 0) {
        return value
    }

    return value * -1
}

export function getChartKeyConfig(key: string) {
    let color: string | null = null
    let zIndex: number | null = null

    switch (key) {
        case "GRID":
            color = "#ff6600"
            zIndex = 5
            break
        case "SOLAR_POWER_PLANT":
            color = "#fff800"
            zIndex = 3
            break
        case "BATTERY":
            color = "#2c88ae"
            zIndex = 3
            break
        case "EV_CHARGER":
            color = "#1250ff"
            zIndex = 1
            break
        case "HVAC":
            color = "#ff00ff"
            zIndex = 1
            break
        case "OTHER":
            color = "#087a00"
            zIndex = 1
            break
        case "PRODUCTION":
            color = "#0ec82c"
            break
        case "CONSUMPTION":
            color = "#ff2222"
            break
    }

    return {
        color,
        zIndex,
    }
}

export function findArrayValueMaxMin(series: any, shouldClamp = true) {
    let yMax = 0
    let yMin = Number.MAX_SAFE_INTEGER

    for (let i = 0; i < series.length; i++) {
        const data = series[i].data

        for (let j = 0; j < data.length; j++) {
            const entry = data[j]
            const value = entry[1]

            if (value > yMax) {
                yMax = value
            }

            if (value < yMin) {
                yMin = value
            }
        }
    }

    if (yMax == 0) {
        yMax = -yMin
    }

    yMax = Math.max(Math.abs(yMax), Math.abs(yMin))

    if (yMax != 0 && yMax < 1000 && shouldClamp) {
        yMax = 1000
    }

    yMin = -yMax

    return {
        max: Math.round(yMax * 100) / 100,
        min: Math.round(yMin * 100) / 100,
    }
}

export function getLegendItemSymbolStyle(item: any) {
    let firstColor = "#fff"
    let lastColor = "#fff"

    switch (item.name) {
        case "productionToGrid":
            firstColor = "#ff8c46"
            lastColor = "#e16724"
            break
        case "productionToHome":
            firstColor = "#9dfdb4"
            lastColor = "#9dfdb4"
            break
        case "productionToBattery":
            firstColor = "#28d5f9"
            lastColor = "#28d5f9"
            break
        case "productionToUnknown":
            firstColor = "#006fff"
            lastColor = "#0079ee"
            break
        case "consumptionFromSolar":
            firstColor = "#9dfdb4"
            lastColor = "#9dfdb4"
            break
        case "consumptionFromBattery":
            firstColor = "#28d5f9"
            lastColor = "#28d5f9"
            break
        case "consumptionFromGrid":
            firstColor = "#ff7c29"
            lastColor = "#d35508"
            break
        case "consumptionFromUnknown":
            firstColor = "#006fff"
            lastColor = "#0079ee"
            break
    }

    return {
        background: `linear-gradient(${firstColor}, ${lastColor})`,
    }
}

export function getLegendItemIcon(item: any) {
    switch (item.name) {
        case "productionToGrid":
            return "icon-system"
        case "productionToHome":
            return "icon-home"
        case "productionToBattery":
            return "icon-battery"
        case "productionToUnknown":
            return "icon-other"
        case "consumptionFromSolar":
            return "icon-sun"
        case "consumptionFromBattery":
            return "icon-battery"
        case "consumptionFromGrid":
            return "icon-system"
        case "consumptionFromUnknown":
            return "icon-other"
        default:
            return ""
    }
}

export function formatValue(
    value: number | null,
    min: number,
    max: number,
    unit: string | null,
    locale: string,
    preserveSign = false,
) {
    if (value === null) {
        return {
            value,
            unit,
        }
    }

    if (unit === null) {
        return {
            value: Intl.NumberFormat(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
            }).format(value),
            unit: null,
        }
    }

    if (["W", "Wh"].includes(unit)) {
        const sign = preserveSign && value < 0 ? -1 : 1

        if (max > 1000000) {
            unit = `M${unit}`
            value = sign * Math.abs(value / 1000000)
        } else if (max > 1000) {
            unit = `k${unit}`
            value = sign * Math.abs(value / 1000)
        } else if (!preserveSign) {
            value = Math.abs(value)
        }

        return {
            value: new Intl.NumberFormat(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(value),
            unit,
        }
    } else if (["%"].includes(unit)) {
        return {
            value: new Intl.NumberFormat(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            }).format(value),
            unit,
        }
    }

    return {
        value,
        unit,
    }
}

export class ChartThemeHelper {
    private readonly _theme: string

    private readonly ThemeConfigs = {
        default: {
            chartBackgroundColor: null,
            chartDataLabelColor: "#141414",
            fontColor: "#fff",
            tooltipFontColor: "#fff",
            tooltipBackgroundColor: "#262626",
            tooltipBorderColor: "#fff",
            gridLineColor: "#6c6c6c",
            series: {
                grid: ["#ff9653", "#f7792c"],
                productionGrid: ["#ff9653", "#f7792c"],
                productionSolar: ["#ffc953", "#f0f72c"],
                productionBattery: ["#53b2ff", "#2c92f7"],
                productionEvCharger: ["#536aff", "#2c55f7"],
                productionToGrid: ["#ff9653", "#f7792c"],
                productionToHome: ["#9dfdb4", "#9dfdb4"],
                productionToBattery: ["#28d5f9", "#28d5f9"],
                productionToUnknown: ["#006fff", "#0079ee"],
                productionToEvCharger: ["#536aff", "#2c55f7"],
                consumptionGrid: ["#ff9653", "#f7792c"],
                consumptionBattery: ["#53b2ff", "#2c92f7"],
                consumptionEvCharger: ["#536aff", "#2c55f7"],
                consumptionHvac: ["#c053ff", "#b32cf7"],
                consumptionOther: ["#319d45", "#098523"],
                consumptionFromSolar: ["#9dfdb4", "#9dfdb4"],
                consumptionFromBattery: ["#28d5f9", "#28d5f9"],
                consumptionFromGrid: ["#ff7c29", "#d35508"],
                consumptionFromUnknown: ["#006fff", "#0079ee"],
                soc: ["#006fff", "#0079ee"],
                supplierEnergySell: ["#9dfdb4", "#9dfdb4"],
                supplierEnergySellFee: ["#006fff", "#0079ee"],
                supplierEnergyBuy: ["#9dfdb4", "#9dfdb4"],
                supplierEnergyBuyFee: ["#006fff", "#0079ee"],
                distributorEnergy: ["#c053ff", "#b32cf7"],
                distributorPower: ["#006fff", "#0079ee"],
                netSell: ["#9dfdb4", "#9dfdb4"],
                combinedBuy: ["#da4a4a", "#ee3434"],
                total: ["#da4a4a", "#ee3434"],
                gauge: [
                    "color-mix(in srgb, var(--theme-shade), white 60%)",
                    "color-mix(in srgb, var(--theme-shade), white 30%)",
                    "var(--theme-shade)",
                ],
            },
        },
        white: {
            fontColor: "#43474b",
            tooltipFontColor: "#fff",
            tooltipBackgroundColor: "#43474b",
            tooltipBorderColor: "#43474b",
            gridLineColor: "#43474b",
        },
        "white-gray": {
            fontColor: "#5C6065",
            tooltipFontColor: "#EEEEED",
            tooltipBackgroundColor: "#5C6065",
            tooltipBorderColor: "#5C6065",
            gridLineColor: "#5C6065",
        },
        "dark-blue": {
            tooltipBackgroundColor: "#001c2f",
            chartDataLabelColor: "#001c2f",
        },
        "white-blue": {
            fontColor: "#2d458c",
            tooltipFontColor: "#fff",
            tooltipBackgroundColor: "#43474b",
            tooltipBorderColor: "#43474b",
            gridLineColor: "#43474b",
        },
        "emerald-white": {
            fontColor: "#005d4e",
            tooltipFontColor: "#fff",
            tooltipBackgroundColor: "#43474b",
            tooltipBorderColor: "#43474b",
        },
        "mid-green": {
            fontColor: "#02d683",
            tooltipFontColor: "#fff",
            tooltipBackgroundColor: "#002a20",
            tooltipBorderColor: "#02d683",
            gridLineColor: "#02d683",
        },
    }

    get theme(): string {
        return this._theme
    }

    constructor(theme: string) {
        this._theme = theme
    }

    private getThemedField(field: string) {
        // @ts-ignore
        const themeConfig = _.merge(
            this.ThemeConfigs.default,
            // @ts-ignore
            this.ThemeConfigs[this.theme] ?? {},
        )

        return themeConfig[field] ?? null
    }

    private getSeriesField(key: string) {
        // @ts-ignore
        const themeConfig = _.merge(
            this.ThemeConfigs.default,
            //@ts-ignore
            this.ThemeConfigs[this.theme] ?? {},
        )

        return themeConfig.series[key] ?? [undefined, undefined]
    }

    getSeriesColorGradient = (seriesKey: string) => {
        return this.getSeriesField(seriesKey)
    }

    get chartBackgroundColor(): string | null {
        return this.getThemedField("backgroundColor")
    }

    get fontColor(): string | null {
        return this.getThemedField("fontColor")
    }

    get tooltipFontColor(): string | null {
        return this.getThemedField("tooltipFontColor")
    }

    get chartDataLabelColor(): string | null {
        return this.getThemedField("chartDataLabelColor")
    }

    get tooltipBackgroundColor(): string | null {
        return this.getThemedField("tooltipBackgroundColor")
    }

    get tooltipBorderColor(): string | null {
        return this.getThemedField("tooltipBorderColor")
    }

    get gridLineColor(): string | null {
        return this.getThemedField("gridLineColor")
    }
}

interface CurrencyInfo {
    name: string
    subunit: string
    subunitDisplay: string
    decimalDigits: number
}

export const currencySubunits: Record<string, CurrencyInfo> = {
    EUR: { name: "Euro", subunit: "cent", decimalDigits: 2, subunitDisplay: "c" },
    GBP: {
        name: "Pound Sterling",
        subunit: "penny",
        decimalDigits: 2,
        subunitDisplay: "p",
    },
    SEK: {
        name: "Swedish Krona",
        subunit: "öre",
        decimalDigits: 2,
        subunitDisplay: "öre",
    },
    NOK: {
        name: "Norwegian Krone",
        subunit: "øre",
        decimalDigits: 2,
        subunitDisplay: "øre",
    },
    DKK: {
        name: "Danish Krone",
        subunit: "øre",
        decimalDigits: 2,
        subunitDisplay: "øre",
    },
    CHF: {
        name: "Swiss Franc",
        subunit: "rappen",
        decimalDigits: 2,
        subunitDisplay: "rappen",
    },
    PLN: {
        name: "Polish Złoty",
        subunit: "grosz",
        decimalDigits: 2,
        subunitDisplay: "grosz",
    },
    CZK: {
        name: "Czech Koruna",
        subunit: "haléř",
        decimalDigits: 2,
        subunitDisplay: "haléř",
    },
    HUF: {
        name: "Hungarian Forint",
        subunit: "fillér",
        decimalDigits: 2,
        subunitDisplay: "fillér",
    },
    RON: {
        name: "Romanian Leu",
        subunit: "ban",
        decimalDigits: 2,
        subunitDisplay: "ban",
    },
    BGN: {
        name: "Bulgarian Lev",
        subunit: "stotinka",
        decimalDigits: 2,
        subunitDisplay: "stotinka",
    },
    HRK: {
        name: "Croatian Kuna",
        subunit: "lipa",
        decimalDigits: 2,
        subunitDisplay: "lipa",
    },
    ISK: {
        name: "Icelandic Króna",
        subunit: "eyrir",
        decimalDigits: 0,
        subunitDisplay: "eyrir",
    },
    UAH: {
        name: "Ukrainian Hryvnia",
        subunit: "kopiyka",
        decimalDigits: 2,
        subunitDisplay: "kopiyka",
    },
    CAD: {
        name: "Canadian Dollar",
        subunit: "cent",
        decimalDigits: 2,
        subunitDisplay: "c",
    },
    USD: {
        name: "United States Dollar",
        subunit: "cent",
        decimalDigits: 2,
        subunitDisplay: "c",
    },
    ILS: {
        name: "Israeli New Shekel",
        subunit: "agora",
        decimalDigits: 2,
        subunitDisplay: "agora",
    },
    RUB: {
        name: "Russian Ruble",
        subunit: "kopeck",
        decimalDigits: 2,
        subunitDisplay: "kopeck",
    },
    TRY: {
        name: "Turkish Lira",
        subunit: "kuruş",
        decimalDigits: 2,
        subunitDisplay: "kuruş",
    },
}

export function getCurrencyInfo(currencyCode: string): CurrencyInfo | null {
    return currencySubunits[currencyCode] || null
}

export function formatCurrencyFraction(
    amount: number,
    currency: string,
    decimals: number | null = null,
): { value: number; unit: string } {
    const currencyInfo = getCurrencyInfo(currency)

    if (!currencyInfo) {
        return {
            value: amount,
            unit: currency,
        }
    }

    const fractionValue = amount * Math.pow(10, currencyInfo.decimalDigits)
    const unit = currencyInfo.subunitDisplay

    if (decimals === null) {
        decimals = currencyInfo.decimalDigits
    }

    const finalValue = Math.floor(fractionValue * Math.pow(10, decimals)) / Math.pow(10, decimals)

    return {
        value: finalValue,
        unit,
    }
}

export function getRandomColorHex() {
    return "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0")
}

//converts decimal number format to HH:mm i.e: 12.75 -> 12:45, 4.25 -> 04:15
export function decimalHourToHHmm(value: number) {
    const rounded = Number(Math.round(Number(value.toString() + "e2")) + "e-2")
    if (rounded === 24.0 || rounded === 24) {
        return "24:00"
    }

    const d = new Date(0, 0)
    d.setSeconds(+rounded * 60 * 60)

    return d.toTimeString().slice(0, 5)
}

export function HHmmToDecimalHour(value: string, returnType?: "string" | "number") {
    const parts = value.split(":").map(Number)

    if (returnType === "string") {
        return (parts[0] + parts[1] / 60).toString()
    }

    return parts[0] + parts[1] / 60
}
